import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import { SITE_URL } from '../config/seo';
import api from '../utils/api';
import { getCityFromHost } from '../utils/getCityFromHost';
import Pagination from '../components/Pagination';
import { stripHtmlTags } from '../utils/htmlUtils';
import { showSuccess, showError } from '../utils/toast';
import { isUserLoggedIn } from '../utils/auth';
import FavouriteButton from '../components/FavouriteButton';
import BusinessCard from '../components/BusinessCard';
import * as LucideIcons from 'lucide-react';
import { getTheme } from '../utils/themes';

const CategoryPage = () => {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const city = getCityFromHost();

  const subcategorySlug = searchParams.get('subcategory');
  const tagSlug = searchParams.get('tag');
  const searchQuery = searchParams.get('search') || '';
  const sortParam = searchParams.get('sort');
  const currentPage = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // First fetch category to get its ID
        const categoryResponse = await api.get(`/categories/${categorySlug}`);
        const categoryData = categoryResponse.data;
        setCategory(categoryData);

        // Build businesses API URL with filters
        let businessesUrl = `/businesses/category/${categorySlug}`;
        const queryParams = [];
        if (subcategorySlug) queryParams.push(`subcategory=${subcategorySlug}`);
        if (tagSlug) queryParams.push(`tag=${tagSlug}`);
        if (searchQuery) queryParams.push(`search=${encodeURIComponent(searchQuery)}`);
        queryParams.push(`sort=${encodeURIComponent(sortParam || 'top-rated')}`);
        queryParams.push(`page=${currentPage}`);
        if (queryParams.length > 0) {
          businessesUrl += `?${queryParams.join('&')}`;
        }

        // Then fetch all other data in parallel
        const [
          businessesResponse,
          categoriesResponse,
          subcategoriesResponse,
          tagsResponse
        ] = await Promise.all([
          api.get(businessesUrl),
          api.get('/categories'),
          api.get(`/subcategories?categoryId=${categoryData._id}`).catch(() => ({ data: [] })),
          api.get(`/tags?categoryId=${categoryData._id}`).catch(() => ({ data: [] }))
        ]);

        const subcategoriesData = subcategoriesResponse.data || [];
        let tagsData = tagsResponse.data || [];
        if (!tagsData.length && businessesResponse.data) {
          const list = businessesResponse.data.businesses || (Array.isArray(businessesResponse.data) ? businessesResponse.data : []);
          const seen = new Map();
          list.forEach(b => {
            (b.tags || []).forEach(t => {
              if (t && t._id && !seen.has(t._id.toString())) {
                seen.set(t._id.toString(), t);
              }
            });
          });
          tagsData = Array.from(seen.values());
        }

        // Handle paginated response
        if (businessesResponse.data && businessesResponse.data.businesses) {
          setBusinesses(businessesResponse.data.businesses);
          setPagination(businessesResponse.data.pagination);
        } else if (Array.isArray(businessesResponse.data)) {
          // Fallback for non-paginated response (backward compatibility)
          setBusinesses(businessesResponse.data);
          setPagination(null);
        } else {
          setBusinesses([]);
          setPagination(null);
        }
        setAllCategories(categoriesResponse.data);
        setSubcategories(subcategoriesData);
        setTags(tagsData);

        // Set selected subcategory and tag
        if (subcategorySlug) {
          const subcat = subcategoriesData.find(s => s.slug === subcategorySlug);
          setSelectedSubcategory(subcat || null);
        } else {
          setSelectedSubcategory(null);
        }

        if (tagSlug) {
          const tag = tagsData.find(t => t.slug === tagSlug);
          setSelectedTag(tag || null);
        } else {
          setSelectedTag(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, subcategorySlug, tagSlug, currentPage, searchQuery, sortParam]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    api.get('/favourites').then((r) => {
      const list = r.data?.favourites || [];
      setFavouriteIds(new Set(list.map((b) => (b._id || b).toString())));
    }).catch(() => { });
  }, []);

  const handleAddFavourite = async (id) => {
    try {
      await api.post(`/favourites/${id}`);
      setFavouriteIds((prev) => new Set([...prev, id.toString()]));
      showSuccess('Added to favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not add to favourites');
    }
  };
  const handleRemoveFavourite = async (id) => {
    try {
      await api.delete(`/favourites/${id}`);
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        next.delete(id.toString());
        return next;
      });
      showSuccess('Removed from favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not remove from favourites');
    }
  };

  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';
  const locationName = cityName || 'Fatehabad';

  const canonical = `/${categorySlug || ''}`;
  const breadcrumbLd = category ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: category.name, item: `${SITE_URL}${canonical}` },
      ...(selectedSubcategory ? [{ '@type': 'ListItem', position: 3, name: selectedSubcategory.name, item: `${SITE_URL}${canonical}?subcategory=${selectedSubcategory.slug}` }] : []),
      ...(selectedTag ? [{ '@type': 'ListItem', position: selectedSubcategory ? 4 : 3, name: selectedTag.name, item: `${SITE_URL}${canonical}?tag=${selectedTag.slug}` }] : []),
    ].slice(0, 4),
  } : null;

  const itemListLd = (category && businesses && businesses.length > 0) ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${category.name} in ${locationName}`,
    itemListElement: businesses.slice(0, 10).map((b, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: b.name,
      url: `${SITE_URL}/b/${b.slug || b._id}`
    }))
  } : null;

  const categoryJsonLd = [
    ...(breadcrumbLd ? [breadcrumbLd] : []),
    ...(itemListLd ? [itemListLd] : []),
    ...(category ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `Where can I find verified ${category.name} in ${locationName}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Digital Fatehabad features verified ${category.name} listings in ${locationName} with authentic contact numbers, addresses, working hours, and real customer reviews.`
          }
        },
        {
          '@type': 'Question',
          name: `How to register a ${category.name} business in ${locationName}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `You can list your ${category.name} shop or service on Digital Fatehabad for free by clicking the 'Submit Business' button.`
          }
        }
      ]
    }] : [])
  ];

  return (
    <>
      <SEO
        title={category?.metaTitle || (category ? `${category.name} in ${locationName} - Best Shops & Services` : 'Category')}
        description={category?.metaDescription || (category ? `Find verified ${category.name} in ${locationName}. Get phone numbers, shop addresses, reviews, and timings for top ${category.name} professionals.` : 'Browse business category')}
        canonical={canonical || undefined}
        jsonLd={categoryJsonLd}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <PageLoader message="Loading category..." />
          </div>
        ) : category ? (
          <>
            {/* Immersive Hero Header */}
            {(() => {
              const theme = getTheme(category.theme);

              return (
                <div className="relative w-full bg-gray-900 -mt-16 pt-16 group overflow-hidden">
                  {/* Background Image / Ambient Gradient */}
                  <div className="absolute inset-0 z-0">
                    {category.image ? (
                      <>
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/20 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/60 via-gray-900/20 to-transparent" />
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gray-900" />
                        <div
                          className="absolute top-0 right-0 w-[800px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none mix-blend-screen"
                          style={{ backgroundColor: theme.colors.primary }}
                        />
                        <div
                          className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[100px] opacity-10 pointer-events-none mix-blend-screen"
                          style={{ backgroundColor: theme.colors.secondary }}
                        />
                        {/* Pattern overlay */}
                        <div className="absolute inset-0 opacity-[0.03]"
                          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
                        />
                      </>
                    )}
                  </div>

                  <div className="container-custom relative z-10 py-16 md:py-24">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center text-sm mb-6 text-gray-300/80 font-medium animate-fadeIn">
                      <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                        <LucideIcons.Home className="w-3.5 h-3.5" />
                        Home
                      </Link>
                      <LucideIcons.ChevronRight className="w-4 h-4 mx-2 text-gray-500" />
                      <span className={`flex items-center gap-2 ${!selectedSubcategory && !selectedTag ? 'text-white' : ''}`}>
                        {/* Show icon only if no image or strictly needed, maybe skip here to keep clean */}
                        {category.name}
                      </span>
                      {selectedSubcategory && (
                        <>
                          <LucideIcons.ChevronRight className="w-4 h-4 mx-2 text-gray-500" />
                          <span className="text-white">{selectedSubcategory.name}</span>
                        </>
                      )}
                      {selectedTag && (
                        <>
                          <LucideIcons.ChevronRight className="w-4 h-4 mx-2 text-gray-500" />
                          <span className="text-white">{selectedTag.name}</span>
                        </>
                      )}
                    </nav>

                    <div className="flex flex-col md:flex-row items-end justify-between gap-8 md:gap-16">
                      <div className="flex-1 max-w-3xl">
                        <div className="flex items-center gap-3 mb-4 animate-slideUp">
                          <span
                            className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-black/40 backdrop-blur-md border border-white/20 text-white shadow-lg"
                            style={{ borderColor: `${theme.colors.primary}40` }}
                          >
                            Category
                          </span>
                          {category.featured && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-yellow-500/30 backdrop-blur-md border border-yellow-500/40 text-yellow-100 shadow-lg flex items-center gap-1.5">
                              <LucideIcons.Star className="w-3 h-3 fill-yellow-100" />
                              Featured
                            </span>
                          )}
                        </div>

                        <div className="inline-block p-1 rounded-2xl bg-black/40 backdrop-blur-md mb-6 animate-slideUp" style={{ animationDelay: '0.1s' }}>
                          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white px-4 py-2 tracking-tight leading-[1.1]" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8), 0 0 40px rgba(0,0,0,0.5)' }}>
                            {category.name}
                          </h1>
                        </div>

                        <div className="inline-block p-5 rounded-xl bg-black/40 backdrop-blur-md border border-white/20 shadow-2xl animate-slideUp" style={{ animationDelay: '0.2s' }}>
                          <p className="text-lg md:text-xl text-white leading-relaxed max-w-2xl font-medium">
                            {selectedSubcategory
                              ? `Browse ${selectedSubcategory.name} in ${locationName}`
                              : selectedTag
                                ? `Browse businesses tagged with "${selectedTag.name}"`
                                : category.description || `Discover the best ${category.name} services and businesses in ${locationName}.`}
                          </p>
                        </div>
                      </div>

                      {/* Glass Stats Card */}
                      <div className="hidden md:flex flex-col gap-4 min-w-[200px] animate-slideUp" style={{ animationDelay: '0.3s' }}>
                        <div className="p-5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 shadow-xl">
                          <div className="flex items-center gap-3 mb-2 text-gray-300">
                            <LucideIcons.Store className="w-5 h-5" />
                            <span className="text-sm font-medium uppercase tracking-wide">Total Listings</span>
                          </div>
                          <div className="text-4xl font-bold text-white tracking-tight">
                            {pagination?.totalItems || businesses.length}
                          </div>
                        </div>

                        {/* Optional CTA or Secondary Stat */}
                        <div className="p-5 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/5 hover:bg-white/10 transition-colors cursor-pointer group/stat">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-400 mb-1 group-hover/stat:text-gray-300 transition-colors">Location</div>
                              <div className="text-lg font-semibold text-white flex items-center gap-1.5">
                                <LucideIcons.MapPin className="w-4 h-4 text-primary-400" />
                                {locationName}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="container-custom py-12">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">

                {/* Main Content */}
                <div className="lg:col-span-3 space-y-8">

                  {/* Control Bar */}
                  <div className="sticky top-20 z-30 bg-white dark:bg-gray-900 p-2 rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-black/50 border border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <LucideIcons.Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={localSearchTerm}
                        onChange={(e) => setLocalSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const newParams = new URLSearchParams(searchParams);
                            if (localSearchTerm.trim()) newParams.set('search', localSearchTerm.trim());
                            else newParams.delete('search');
                            newParams.delete('page');
                            setSearchParams(newParams);
                          }
                        }}
                        placeholder="Search businesses..."
                        className="w-full h-12 pl-12 pr-4 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-500 text-base"
                      />
                    </div>
                    <div className="flex items-center gap-2 pl-2 pr-2 border-t sm:border-t-0 sm:border-l border-gray-100 dark:border-gray-800 pt-2 sm:pt-0">
                      <button
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          if (localSearchTerm.trim()) newParams.set('search', localSearchTerm.trim());
                          else newParams.delete('search');
                          newParams.delete('page');
                          setSearchParams(newParams);
                        }}
                        className="h-10 px-6 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors text-sm"
                      >
                        Search
                      </button>
                      {(searchQuery || selectedSubcategory || selectedTag) && (
                        <Link
                          to={`/${categorySlug}`}
                          onClick={() => setLocalSearchTerm('')}
                          className="h-10 w-10 flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          title="Clear Filters"
                        >
                          <LucideIcons.X className="w-5 h-5" />
                        </Link>
                      )}
                      <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>
                      <select
                        value={sortParam || 'top-rated'}
                        onChange={(e) => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.set('sort', e.target.value);
                          newParams.delete('page');
                          setSearchParams(newParams);
                        }}
                        className="h-10 bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300 text-sm font-medium cursor-pointer"
                      >
                        <option value="top-rated">⭐ Top Rated</option>
                        <option value="name:asc">A-Z</option>
                        <option value="name:desc">Z-A</option>
                        <option value="createdAt:desc">Newest</option>
                      </select>
                    </div>
                  </div>

                  {/* Results Info */}
                  {searchQuery && (
                    <div className="text-gray-600 dark:text-gray-400">
                      Found {businesses.length} results for "<span className="text-gray-900 dark:text-white font-medium">{searchQuery}</span>"
                    </div>
                  )}

                  {/* Businesses Grid */}
                  {businesses.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-0 md:gap-6 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800">
                      {businesses.map((business) => (
                        <BusinessCard
                          key={business._id}
                          business={business}
                          favouriteIds={favouriteIds}
                          onAddFavourite={handleAddFavourite}
                          onRemoveFavourite={handleRemoveFavourite}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 text-center border border-gray-100 dark:border-gray-800">
                      <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                        <LucideIcons.SearchX className="w-10 h-10 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No businesses found</h3>
                      <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto mb-8">
                        We couldn't find any businesses matching your current filters. Try adjusting your search or clearing filters.
                      </p>
                      {(selectedSubcategory || selectedTag || searchQuery) && (
                        <Link
                          to={`/${categorySlug}`}
                          onClick={() => setLocalSearchTerm('')}
                          className="px-6 py-3 bg-gray-900 dark:bg-gray-700 text-white rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                        >
                          Clear All Filters
                        </Link>
                      )}
                    </div>
                  )}

                  {/* Pagination */}
                  {pagination && pagination.totalPages > 1 && (
                    <div className="pt-8">
                      <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        totalItems={pagination.totalItems}
                        itemsPerPage={pagination.itemsPerPage}
                        itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                        hasNextPage={pagination.hasNextPage}
                        hasPrevPage={pagination.hasPrevPage}
                        nextPage={pagination.nextPage}
                        prevPage={pagination.prevPage}
                        baseUrl={`/${categorySlug}`}
                        queryParams={{
                          ...(subcategorySlug && { subcategory: subcategorySlug }),
                          ...(tagSlug && { tag: tagSlug }),
                          ...(searchQuery && { search: searchQuery }),
                          ...(sortParam && { sort: sortParam })
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1 space-y-8">
                  <div className="sticky top-32 space-y-6">

                    {/* Related Categories Widget */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md shadow-gray-100/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-800">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <LucideIcons.Compass className="w-5 h-5 text-primary-500" />
                        Explore More
                      </h3>
                      <div className="space-y-1">
                        {allCategories
                          .filter(cat => cat.slug !== categorySlug)
                          .slice(0, 8)
                          .map((cat) => (
                            <Link
                              key={cat._id}
                              to={`/${cat.slug}`}
                              className="group flex items-center justify-between p-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-primary-600 dark:hover:text-primary-400 transition-all"
                            >
                              <span className="flex items-center gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 group-hover:bg-primary-500 transition-colors"></span>
                                {cat.name}
                              </span>
                              <LucideIcons.ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1" />
                            </Link>
                          ))}
                      </div>
                      <Link
                        to="/"
                        className="mt-4 block text-center text-sm font-semibold text-primary-600 hover:text-primary-700 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors"
                      >
                        View All Categories
                      </Link>
                    </div>

                    {/* Subcategories Widget */}
                    {subcategories.length > 0 && (
                      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md shadow-gray-100/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-800">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <LucideIcons.Layers className="w-5 h-5 text-primary-500" />
                          Subcategories
                        </h3>
                        <div className="space-y-1">
                          <Link
                            to={`/${categorySlug}${tagSlug ? `?tag=${tagSlug}` : ''}`}
                            className={`block py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${!selectedSubcategory
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                          >
                            All {category.name}
                          </Link>
                          {subcategories.map((subcat) => (
                            <Link
                              key={subcat._id}
                              to={`/${categorySlug}?subcategory=${subcat.slug}${tagSlug ? `&tag=${tagSlug}` : ''}`}
                              className={`block py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${selectedSubcategory?._id === subcat._id
                                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                            >
                              {subcat.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags Widget */}
                    {tags.length > 0 && (
                      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md shadow-gray-100/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-800">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <LucideIcons.Tag className="w-5 h-5 text-primary-500" />
                          Popular Tags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/${categorySlug}${subcategorySlug ? `?subcategory=${subcategorySlug}` : ''}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${!selectedTag
                              ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100'
                              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                              }`}
                          >
                            All
                          </Link>
                          {tags.slice(0, 15).map((tag) => (
                            <Link
                              key={tag._id}
                              to={`/${categorySlug}?tag=${tag.slug}${subcategorySlug ? `&subcategory=${subcategorySlug}` : ''}`}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${selectedTag?._id === tag._id
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:text-primary-600 dark:hover:text-primary-400'
                                }`}
                            >
                              {tag.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                </div>

              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-white dark:bg-gray-900">
            <div className="w-24 h-24 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6 animate-pulse">
              <LucideIcons.FolderX className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Category Not Found</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-8 text-lg">
              We couldn't find the category you're looking for. It might have been moved or deleted.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-all hover:scale-105 shadow-lg shadow-primary-500/30">
              <LucideIcons.ArrowLeft className="w-5 h-5" />
              Return to Home
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default CategoryPage;

